import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DegreeSlider from '../Visualeffects/DegreeSlider';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';

export default function PolynomialFitUI({ state, setState }) {
  const {
    pointsInput,
    result,
    error,
    plotData,
    loading,
    degree = 1, // default linear
  } = state;

  const set = (key, value) => setState(prev => ({ ...prev, [key]: value }));

  const [xMin, xMax] = result?.x_limits || [0, 1];
  const [yMin, yMax] = result?.y_limits || [0, 1];
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xPad = 0.2 * xRange;
  const yPad = 0.1 * yRange || 1;

  const handleFit = async () => {
    set('loading', true);
    set('error', null);
    set('result', null);
    set('plotData', []);

    try {
      const response = await fetch('https://api.numsolve.org/poly-fit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          points: JSON.parse(pointsInput),
          degree: degree,
        }),
      });


      const data = await response.json(); // 👈 Hier kommt `data`

      if (data.error) {
        set('error', data.error);
      } else {
        // 💡 Hier transformieren wir die Keys, damit Recharts klarkommt
        set('result', {
          ...data,
          points: data.points.map(p => ({ x: p.x, originalY: p.y })),
        });

        set('plotData', data.plot.map(p => ({ x: p.x, fittedY: p.y }))); try {
          const recentEntry = {
            timestamp: new Date().toISOString(),
            method: 'linearfit', // passt zu deiner Tab-ID & Farbzuordnung
            input: {
              points: JSON.parse(pointsInput),
              degree,
            },
            // quick overview for export
            log: [
              {
                kind: 'summary',
                equation: data.line ?? data.equation ?? null,
                coefficients: Array.isArray(data.coefficients) ? data.coefficients : null,
                average_error: data.average_error ?? null,
                max_error: data.max_error ?? null,
              },
            ],
          };
          const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
          const updated = [recentEntry, ...existing].slice(0, 100);
          localStorage.setItem('calcLogs', JSON.stringify(updated));
          window.dispatchEvent(new Event('calcLogsUpdated'));
        } catch { }
      }
    } catch (e) {
      set('error', 'Backend not reachable.');
    }

    set('loading', false);
  };

  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <div className="space-y-4">
        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Data Points</label>
          <div className="input-wrapper input-pulse-yellow">
            <textarea
              value={pointsInput}
              onChange={(e) => set('pointsInput', e.target.value)}
              placeholder="e.g. [[0, 1], [1, 2], [2, 1.5], [3, 4.0], [6, 2]]"
              rows={4}
              className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-4">
          <DegreeSlider value={degree} onChange={(val) => set('degree', val)} />
        </div>

        <div className="mt-4">
          <button className="button button-yellow" onClick={handleFit} disabled={loading}>
            {loading ? 'Polynomial Fitting…' : 'Solve'}
          </button>
        </div>
      </div>
    </div>
  );

  const outputCard = (
    <div
      className={`rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] transition-all duration-300 ${
        result && !error ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-800">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!result && !error && !loading && (
        <div className="text-sm text-gray-500">No results yet. Please enter some data points.</div>
      )}

      {result?.equation && (
        <div className="mb-4 text-sm text-gray-800">
          <p className="font-semibold">Best Fit Line:</p>
          <pre className="mt-2 whitespace-pre-wrap break-words rounded border border-gray-300 bg-white p-3">
            {result.equation}
          </pre>
        </div>
      )}

      {result?.line && (
        <div className="mb-4 text-sm text-gray-800">
          <p className="font-semibold">Fitted Graph:</p>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded border border-gray-300 bg-white p-3">
            {result.line}
          </pre>
        </div>
      )}

      {result?.average_error !== undefined && (
        <div className="mt-4 text-sm text-gray-800">
          <p className="mb-1 font-semibold">Fit Error Metrics:</p>
          <pre className="rounded border border-gray-300 bg-white p-3">
            Average Error: {result.average_error.toFixed(4)}{'\n'}
            Max Error: {result.max_error.toFixed(4)}
          </pre>
        </div>
      )}

      {plotData?.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-md font-semibold text-gray-800">Plot</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="x"
                domain={[xMin - xPad, xMax + xPad]}
                tick={{ fontSize: 14 }}
                tickFormatter={(val) =>
                  Math.abs(val) > 9999 || (Math.abs(val) <= 0.0001 && Math.abs(val) !== 0)
                    ? val.toExponential(2)
                    : val.toFixed(2)
                }
              />
              <YAxis
                type="number"
                domain={[yMin - yPad, yMax + yPad]}
                tick={{ fontSize: 14 }}
                tickFormatter={(val) =>
                  Math.abs(val) > 9999 || (Math.abs(val) <= 0.0001 && Math.abs(val) !== 0)
                    ? val.toExponential(2)
                    : val.toFixed(2)
                }
              />
              <Tooltip formatter={(value, name) => [value.toFixed(2), name]} labelFormatter={(label) => `x = ${label.toFixed(2)}`} />

              <Line
                type="monotone"
                data={plotData}
                dataKey="fittedY"
                stroke="#facc15"
                strokeWidth={2}
                dot={false}
                name="Fitted Line"
              />

              <Line
                type="monotone"
                data={result.points}
                dataKey="originalY"
                stroke="#000"
                strokeWidth={0}
                dot={{ r: 3, fill: '#000' }}
                name="Data Point"
                legendType="circle"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout
      loading={loading}
      inputCard={inputCard}
      outputCard={outputCard}
    />
  );
}